import { useCallback, useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { track } from "@/lib/analytics";
import { userHasActed, autoSoundAllowed, markUserMuted, claimSpeaker, releaseSpeaker, waitForGesture, stopWaiting } from "@/lib/autoSound";

/**
 * HomeVideo — the 25-second Formanti film, in the shape that fits the screen.
 *
 * One film, two cuts: 16:9 for computers and tablets (md and up), 9:16 for phones. The right cut is
 * chosen from the viewport and follows it if the window is resized or the device is rotated.
 *
 * Built so it costs nothing until it is wanted:
 *   - no video bytes until the player is near the screen (the poster is all that loads with the page);
 *   - it autoplays muted, looped, inline, only while on screen, and pauses when scrolled away;
 *   - it does not autoplay at all for people who asked for reduced motion or turned on data saver:
 *     they see the poster and a play button;
 *   - tapping it the first time turns the sound on from the start; after that a tap plays and pauses.
 *
 * The files live in public/videos (see .vercelignore and .gitignore: *.mp4 is ignored by default and these
 * are the exceptions). Names carry a version: bump -v1 when the film changes so caches pick up the new one.
 */

const CUTS = {
  wide: { src: "/videos/formanti-teaser-16x9-v1.mp4", poster: "/videos/formanti-teaser-16x9-v1.jpg", label: "16x9" },
  tall: { src: "/videos/formanti-teaser-9x16-v1.mp4", poster: "/videos/formanti-teaser-9x16-v1.jpg", label: "9x16" },
};

const evName = (film, name) => (film?.event ? `${film.event}_${name}` : `home_video_${name}`);

function useWide() {
  const query = "(min-width: 768px)";
  const [wide, setWide] = useState(() => (typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : true));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const on = () => setWide(mq.matches);
    on();
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => (mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on));
  }, []);
  return wide;
}

// People who asked for less motion, turned on data saver, or are on a slow connection get the poster and a play button.
const quiet = () => {
  try {
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const c = navigator.connection;
    const slow = !!c && /^(slow-2g|2g|3g)$/.test(c.effectiveType || "");
    return !!(reduce || c?.saveData === true || slow);
  } catch { return false; }
};

/**
 * `film` shows another film in this same player: { src, poster, label, event, alt }, one square cut for every screen (the
 * practice film). Its analytics events are `${event}_play` and `${event}_sound`; without it, everything is as before.
 */
export default function HomeVideo({ className = "", eager = false, testId = "home-video", film = null, tallHeight = "min(78svh, 640px)", compact = false }) {
  const wide = useWide();
  const cut = film || (wide ? CUTS.wide : CUTS.tall);
  const boxRef = useRef(null);
  const videoRef = useRef(null);
  const trackedPlay = useRef(false);
  const [near, setNear] = useState(false); // close enough to start fetching
  const [onScreen, setOnScreen] = useState(false); // enough of it visible to play
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [blocked, setBlocked] = useState(false); // the browser refused to autoplay: show the play button
  const [calm] = useState(quiet); // true: no autoplay and nothing fetched until the person asks
  const [asked, setAsked] = useState(false); // the person pressed play
  const [ready, setReady] = useState(!eager); // the hero film waits for the page to finish loading

  // the hero film is at the top of the page: let the page and its scripts finish first
  useEffect(() => {
    if (!eager) return undefined;
    let t;
    const go = () => { t = setTimeout(() => setReady(true), 500); };
    if (document.readyState === "complete") go(); else window.addEventListener("load", go, { once: true });
    return () => { clearTimeout(t); window.removeEventListener("load", go); };
  }, [eager]);

  // when the player gets near, then when it is mostly visible
  useEffect(() => {
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === "undefined") { setNear(true); setOnScreen(true); return undefined; }
    const ioNear = new IntersectionObserver(([e]) => { if (e.isIntersecting) setNear(true); }, { rootMargin: "200px 0px" });
    const ioSee = new IntersectionObserver(([e]) => setOnScreen(e.isIntersecting && e.intersectionRatio >= 0.4), { threshold: [0, 0.4, 0.7] });
    ioNear.observe(box); ioSee.observe(box);
    return () => { ioNear.disconnect(); ioSee.disconnect(); };
  }, []);

  const load = (near && ready && !calm) || asked; // whether the file is fetched at all

  // the muted flag is a property, not just an attribute
  useEffect(() => { if (videoRef.current) videoRef.current.muted = muted; }, [muted, cut.src]);

  // autoplay while it is on screen; pause when it is not
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !load) return;
    if (asked) { v.muted = false; setMuted(false); v.play().catch(() => {}); return; }
    if (onScreen && !calm) {
      const p = v.play();
      if (p && p.catch) p.catch(() => setBlocked(true));
    } else if (!onScreen) {
      v.pause();
    }
  }, [onScreen, load, asked, calm, cut.src]);

  const onPlay = useCallback(() => {
    setPlaying(true); setBlocked(false);
    if (!trackedPlay.current) { trackedPlay.current = true; track(evName(film, "play"), { cut: cut.label, muted: videoRef.current?.muted !== false }); }
  }, [cut.label, film]);

  // back to muted (another film took over, scrolled away, or the person tapped mute)
  const quiet2 = useCallback(() => { const v = videoRef.current; if (v) v.muted = true; setMuted(true); releaseSpeaker(quiet2); }, []);
  const soundId = useRef(`hv${Math.random().toString(36).slice(2)}`).current;
  useEffect(() => () => { releaseSpeaker(quiet2); stopWaiting(soundId); }, [quiet2, soundId]);

  const toggleSound = useCallback((e) => {
    e?.stopPropagation();
    const v = videoRef.current; if (!v) return;
    const next = !v.muted;
    if (next) { markUserMuted(); stopWaiting(soundId); releaseSpeaker(quiet2); } else claimSpeaker(quiet2);
    v.muted = next; setMuted(next);
    if (!next) { track(evName(film, "sound"), { cut: cut.label }); if (v.paused) v.play().catch(() => {}); }
  }, [cut.label, film, quiet2, soundId]);

  // the practice film carries the app's real coach voice: sound on by default where the browser allows it (lib/autoSound.js)
  const speakNow = useCallback((by) => {
    const v = videoRef.current; if (!v) return;
    claimSpeaker(quiet2);
    v.muted = false;
    try { v.currentTime = 0; } catch { /* starts at 0 anyway */ }
    const p = v.play();
    const ok = () => { setMuted(false); track(evName(film, "sound"), { cut: cut.label, by }); };
    const refused = () => { v.muted = true; setMuted(true); releaseSpeaker(quiet2); v.play().catch(() => {}); if (by !== "tap") waitForGesture(soundId, speakNow); };
    if (p && p.then) p.then(ok).catch(refused); else ok();
  }, [cut.label, film, quiet2, soundId]);
  useEffect(() => {
    if (!film || !playing || !onScreen || !muted || calm || !autoSoundAllowed()) { stopWaiting(soundId); return undefined; }
    if (userHasActed()) speakNow("auto"); else waitForGesture(soundId, speakNow);
    return () => stopWaiting(soundId);
  }, [film, playing, onScreen, muted, calm, soundId, speakNow]);
  useEffect(() => { if (!onScreen && videoRef.current && !videoRef.current.muted) quiet2(); }, [onScreen, quiet2]);

  // first tap while muted: sound on, from the start. Afterwards: play / pause.
  const onTap = useCallback(() => {
    const v = videoRef.current; if (!v) return;
    if (!load) { setAsked(true); return; } // nothing was fetched yet: fetch it now, with sound
    if (v.muted) { claimSpeaker(quiet2); v.currentTime = 0; v.muted = false; setMuted(false); v.play().catch(() => {}); track(evName(film, "sound"), { cut: cut.label, by: "tap" }); return; }
    if (v.paused) v.play().catch(() => {}); else v.pause();
  }, [cut.label, load, film, quiet2]);

  const fullscreen = useCallback((e) => {
    e.stopPropagation();
    const v = videoRef.current; if (!v) return;
    if (v.requestFullscreen) v.requestFullscreen().catch(() => {});
    else if (v.webkitRequestFullscreen) v.webkitRequestFullscreen();
    else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iPhone Safari
  }, []);

  // while it plays muted: say how to hear it
  const soundHint = playing && muted && (
    <button type="button" onClick={toggleSound}
      className={`${film ? "" : "absolute left-3 bottom-3 "}inline-flex items-center gap-1.5 min-h-11 rounded-full bg-black/70 px-3.5 text-[13px] font-bold text-white backdrop-blur-sm border border-white/15`}>
      <VolumeX className="h-4 w-4" aria-hidden="true" /> Tap for sound
    </button>
  );

  const controls = (
        <div className={film ? "flex items-center gap-2" : "absolute right-2.5 bottom-3 flex items-center gap-2"}>
          <button type="button" onClick={(e) => { e.stopPropagation(); const v = videoRef.current; if (v) (v.paused ? v.play() : v.pause()); }} aria-label={playing ? "Pause" : "Play"}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/65 text-white border border-white/15">
            {playing ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4 ml-0.5" aria-hidden="true" />}
          </button>
          <button type="button" onClick={toggleSound} aria-label={muted ? "Turn sound on" : "Turn sound off"}
            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/65 text-white border border-white/15">
            {muted ? <VolumeX className="h-4 w-4" aria-hidden="true" /> : <Volume2 className="h-4 w-4" aria-hidden="true" />}
          </button>
          <button type="button" onClick={fullscreen} aria-label="Full screen"
            className="flex h-11 w-11 items-center justify-center rounded-full bg-black/65 text-white border border-white/15">
            <Maximize2 className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
  );

  const frame = film && film.aspect === "9 / 16"
    ? { className: "mx-auto", style: { height: "min(86svh, 780px, calc((100vw - 2rem) * 16 / 9))", aspectRatio: "9 / 16" } } // a vertical film (never wider than the screen) (phone-shaped, like the home page's phone cut)
    : film
    ? { className: "w-full max-w-[560px] aspect-square", style: undefined }
    : wide
    ? { className: "w-full max-w-[1000px] aspect-video", style: undefined }
    : { className: "mx-auto", style: { height: tallHeight, aspectRatio: "9 / 16" } };

  return (
    <div className={`relative ${className}`}>
      <div className="pointer-events-none absolute -inset-6 sm:-inset-10 bg-lime-400/10 blur-3xl rounded-full" aria-hidden="true" />
      <div
        ref={boxRef}
        className={`relative mx-auto overflow-hidden rounded-3xl border border-zinc-800 bg-black shadow-2xl shadow-black/60 ${frame.className}`}
        style={frame.style}
        data-testid={testId}
      >
        <video
          key={cut.src}
          ref={videoRef}
          className="absolute inset-0 h-full w-full object-contain bg-black cursor-pointer"
          src={load ? cut.src : undefined}
          poster={cut.poster}
          preload={load ? "metadata" : "none"}
          muted
          loop
          playsInline
          aria-label={film?.alt || "Formanti in 25 seconds: film a rally, see the breakdown, your pose in 3D, practise the fix"}
          onClick={onTap}
          onPlay={onPlay}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress(v.currentTime / v.duration); }}
        />

        {/* paused (or autoplay refused): a clear play button */}
        {!playing && (
          <button type="button" onClick={onTap} aria-label="Play the video"
            className="absolute inset-0 flex items-center justify-center bg-black/10 hover:bg-black/0 transition-colors">
            <span className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-lime-400 text-black shadow-lg shadow-lime-400/30">
              <Play className="h-7 w-7 sm:h-9 sm:w-9 ml-1" fill="currentColor" aria-hidden="true" />
            </span>
          </button>
        )}

        {!film && !compact && soundHint /* compact (the small phone hero): the label above the film says how to hear it, and the frame has no room for both */}
        {!film && controls}

        <div className="absolute inset-x-0 bottom-0 h-1 bg-white/10" aria-hidden="true"><div className="h-full bg-lime-400" style={{ width: `${progress * 100}%` }} /></div>
        {blocked && <span className="sr-only">Autoplay was blocked by your browser. Press play to watch.</span>}
      </div>
      {film && (
        <div className="mx-auto mt-3 flex w-full max-w-[560px] items-center justify-between gap-2" data-testid="film-controls">
          <div className="min-h-11">{soundHint}</div>
          {controls}
        </div>
      )}
    </div>
  );
}
